import type { DraftDocument } from "../shared/types";

const UNDO_PREFIX = "bchannel:undo:";
const REDO_PREFIX = "bchannel:redo:";
export const MAX_UNDO = 60;
export const MAX_REDO = 30;
const MAX_BYTES = 2_000_000;

export type HistorySnapshot = { document: DraftDocument; title: string; at: number };

function readStack(key: string): HistorySnapshot[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is HistorySnapshot =>
        Boolean(item) && typeof item === "object" && "document" in item && "title" in item,
    );
  } catch {
    return [];
  }
}

function writeStack(key: string, stack: HistorySnapshot[], maxEntries: number) {
  try {
    let slice = stack.slice(-maxEntries);
    // Keep a healthy number of entries, dropping the oldest if the document
    // is huge enough that the history would exceed the storage budget.
    while (slice.length > 1 && JSON.stringify(slice).length > MAX_BYTES) slice = slice.slice(1);
    localStorage.setItem(key, JSON.stringify(slice));
  } catch {
    // Quota exceeded or storage disabled: history degrades to in-memory only.
  }
}

export function loadHistory(draftId: string) {
  return {
    undo: readStack(`${UNDO_PREFIX}${draftId}`),
    redo: readStack(`${REDO_PREFIX}${draftId}`),
  };
}

export function saveHistory(draftId: string, undo: HistorySnapshot[], redo: HistorySnapshot[]) {
  writeStack(`${UNDO_PREFIX}${draftId}`, undo, MAX_UNDO);
  writeStack(`${REDO_PREFIX}${draftId}`, redo, MAX_REDO);
}

export function clearHistory(draftId: string) {
  try {
    localStorage.removeItem(`${UNDO_PREFIX}${draftId}`);
    localStorage.removeItem(`${REDO_PREFIX}${draftId}`);
  } catch {
    // ignore
  }
}
